/**
 * The contractor's edits to one surface's result — `review` on
 * structures/{sid}/existingConditions/{targetId}, the one field the rules let
 * the owner, an org editor or an admin write — as the inspection report reads
 * them (claude-docs/2026-10-15-inspection-report-design.md).
 *
 * A review counts only for the run it was made on. A re-run re-mints finding
 * ids (`f1`, `f2`, … per doc) and the depth service carries `review` over, so a
 * hide made on an earlier run would hide whatever holds that id now. Pure.
 */
const { GRADES } = require('./vocabulary');

const NONE = Object.freeze({
  grade: null,
  hiddenFindings: Object.freeze([]),
  hiddenPhotos: Object.freeze([]),
  hiddenDevices: Object.freeze([]),
  notFixed: false,
});

const ids = (v) =>
  Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [];

/** The edits that apply to `doc` now: its review when made on this run, else none. */
function activeReview(doc) {
  const r = doc && doc.review;
  if (!r || !doc.runId || r.runId !== doc.runId) return NONE;
  return {
    grade: GRADES.includes(r.grade) ? r.grade : null,
    hiddenFindings: ids(r.hiddenFindings),
    hiddenPhotos: ids(r.hiddenPhotos),
    // devices of this doc the contractor says are wrong or doubled
    hiddenDevices: ids(r.hiddenDevices),
    // an object the scanner called fixed that is not (a dresser as storage)
    notFixed: r.notFixed === true,
  };
}

/** True when `doc` carries edits made on an earlier run, which no longer count. */
function isStaleReview(doc) {
  return Boolean(doc && doc.review && doc.review.runId !== doc.runId);
}

const toggled = (list, id, on) =>
  on ? [...new Set([...list, id])] : list.filter((x) => x !== id);

/**
 * The `review` to write after one edit, stamped with the doc's run and the
 * editor's uid. `edit` is one of `{ hideFinding: [id, on] }`,
 * `{ hidePhoto: [id, on] }`, `{ hideDevice: [id, on] }`, `{ notFixed: bool }`
 * or `{ grade }` (a grade, or null for the model's).
 */
function editReview(doc, edit, uid) {
  const cur = activeReview(doc);
  let { grade, hiddenFindings, hiddenPhotos, hiddenDevices, notFixed } = cur;
  if (edit.hideFinding)
    hiddenFindings = toggled(hiddenFindings, ...edit.hideFinding);
  if (edit.hidePhoto) hiddenPhotos = toggled(hiddenPhotos, ...edit.hidePhoto);
  if (edit.hideDevice)
    hiddenDevices = toggled(hiddenDevices, ...edit.hideDevice);
  if ('notFixed' in edit) notFixed = edit.notFixed === true;
  if ('grade' in edit) {
    if (edit.grade !== null && !GRADES.includes(edit.grade)) {
      throw new Error(`Not a grade: ${edit.grade}`);
    }
    grade = edit.grade;
  }
  return {
    runId: doc.runId,
    grade,
    hiddenFindings: [...hiddenFindings],
    hiddenPhotos: [...hiddenPhotos],
    hiddenDevices: [...hiddenDevices],
    notFixed,
    updatedBy: uid,
  };
}

module.exports = { activeReview, isStaleReview, editReview };
